import { useState } from "react";
import { IconShield, IconShieldCheck, IconAlertTriangle } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Input } from "../../../../components/common/input";
import { Switch } from "../../../../components/common/switch";
import { useMe } from "../../../../swr";
import { twoFactorService } from "../../../api_client";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { ChangePasswordSection } from "./change_password_section";

function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = phone.replace(/\s/g, "");
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 3)}•••${digits.slice(-2)}`;
}

function formatLastChanged(date: string | Date | null | undefined): string {
  if (!date) return "Never changed";
  const d = typeof date === "string" ? new Date(date) : date;
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 1) return "Changed today";
  if (days < 30) return `Last changed ${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Last changed ${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(months / 12);
  return `Last changed ${years} year${years === 1 ? "" : "s"} ago`;
}

export function SecuritySection() {
  const { user, mutate } = useMe();
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const hasVerifiedPhone = !!user?.phoneNumber && !!user?.phoneVerified;

  // 2FA enable/disable confirmation flow.
  const [twoFaState, setTwoFaState] = useState<"idle" | "awaiting-enable-code" | "awaiting-disable-code">("idle");
  const [code, setCode] = useState("");
  const [twoFaBusy, setTwoFaBusy] = useState(false);

  const startEnable = async () => {
    setTwoFaBusy(true);
    try {
      await twoFactorService.twoFactorEnable();
      showSuccessToast("Verification code sent via SMS");
      setTwoFaState("awaiting-enable-code");
    } catch (err) {
      showErrorToast("Failed to send verification code", err);
    } finally {
      setTwoFaBusy(false);
    }
  };

  const startDisable = async () => {
    setTwoFaBusy(true);
    try {
      await twoFactorService.twoFactorDisable();
      showSuccessToast("Verification code sent via SMS");
      setTwoFaState("awaiting-disable-code");
    } catch (err) {
      showErrorToast("Failed to send verification code", err);
    } finally {
      setTwoFaBusy(false);
    }
  };

  const confirmCode = async () => {
    if (!code) return;
    setTwoFaBusy(true);
    try {
      if (twoFaState === "awaiting-enable-code") {
        await twoFactorService.twoFactorEnableConfirm({ code });
        showSuccessToast("Two-factor authentication enabled");
      } else if (twoFaState === "awaiting-disable-code") {
        await twoFactorService.twoFactorDisableConfirm({ code });
        showSuccessToast("Two-factor authentication disabled");
      }
      setTwoFaState("idle");
      setCode("");
      await mutate();
    } catch (err) {
      showErrorToast("Invalid or expired code", err);
    } finally {
      setTwoFaBusy(false);
    }
  };

  const cancel2fa = () => {
    setTwoFaState("idle");
    setCode("");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite overflow-hidden">
        <div className="flex items-center gap-4 px-6 pt-5 pb-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-bgMuted">
            <IconShield size={28} className="text-textMuted" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Heading variant="h3" tone="dark">Password</Heading>
            <Text variant="bodySm" tone="dark">Manage your account password</Text>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-6 pb-5">
          <div className="flex items-center justify-between rounded-2xl border border-borderDefault px-4 py-5">
            <div className="flex flex-col gap-0.5">
              <Text variant="bodyBold" tone="dark">Password</Text>
              <Text variant="bodySm" tone="dark">{formatLastChanged(user?.passwordChangedAt)}</Text>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="!bg-bgWhite"
              onClick={() => setShowPasswordForm((v) => !v)}
            >
              {showPasswordForm ? "Close" : "Change Password"}
            </Button>
          </div>
          {showPasswordForm && (
            <div className="rounded-2xl border border-borderDefault px-4 py-5">
              <ChangePasswordSection />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite overflow-hidden">
        <div className="flex items-center gap-4 px-6 pt-5 pb-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-bgMuted">
            <IconShieldCheck size={28} className="text-textMuted" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Heading variant="h3" tone="dark">Two-Factor Authentication</Heading>
            <Text variant="bodySm" tone="dark">
              {user?.twoFactorEnabled
                ? "An SMS verification code is required at sign-in."
                : "Add an extra layer of security with an SMS code at sign-in."}
            </Text>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-6 pb-5">
          <div className="flex items-center justify-between rounded-2xl border border-borderDefault px-4 py-5">
            <div className="flex flex-col gap-0.5">
              <Text variant="bodyBold" tone="dark">SMS-based 2FA</Text>
              <Text variant="bodySm" tone="dark">
                {hasVerifiedPhone
                  ? `Codes will be sent to ${maskPhone(user?.phoneNumber)}.`
                  : "Add and verify a phone number to enable SMS 2FA."}
              </Text>
            </div>
            <Switch
              checked={!!user?.twoFactorEnabled}
              disabled={
                twoFaBusy || twoFaState !== "idle" || (!hasVerifiedPhone && !user?.twoFactorEnabled)
              }
              onChange={(next) => (next ? startEnable() : startDisable())}
              aria-label="Two-factor authentication"
            />
          </div>

          {!hasVerifiedPhone && !user?.twoFactorEnabled && (
            <div className="flex items-start gap-3 rounded-2xl border border-accentAmber bg-accentYellowTint px-4 py-3">
              <IconAlertTriangle size={18} className="mt-0.5 shrink-0 text-accentAmber" />
              <div className="flex flex-1 flex-col gap-0.5">
                <Text variant="bodySmBold" tone="amber">No verified phone number on this account</Text>
                <Text variant="bodyXs" tone="amber">
                  Add a phone number to your profile and verify it before enabling SMS-based two-factor authentication.
                </Text>
              </div>
            </div>
          )}

          {twoFaState !== "idle" && (
            <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault px-4 py-5">
              <Text variant="bodySm" tone="dark">
                Enter the 6-digit code we just sent to {maskPhone(user?.phoneNumber) || "your phone"}.
              </Text>
              <Input
                label="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={cancel2fa} disabled={twoFaBusy}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={confirmCode}
                  disabled={twoFaBusy || code.length < 4}
                >
                  {twoFaBusy ? "Verifying…" : "Verify"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
